import React, { useRef, forwardRef, type ElementType } from 'react';
import clsx from 'clsx';

import {
  MButtonProps,
  MButtonType,
  MButtonSize,
  MButtonIconPlacement,
  MButtonIconClass,
  MButtonRef,
} from './MButton.types';
import { createStyledButton, MButtonLabel } from './MButton.styles';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { AIcon, AIconSize } from '@marriott/mi-web-ui-kit';
import { useTracking } from '../../hooks/useTracking';

const COMPONENT_NAME = 'm-cobrand-MButton';
const TEST_ID = 'cobrand-MButton';

const MButtonContent: React.FC<{
  variant: MButtonType;
  size: MButtonSize;
  iconPlacement: MButtonIconPlacement;
  iconClass?: string;
  aIcon?: { size?: string; icon: string };
  isExternalLink: boolean;
  children: React.ReactNode;
}> = ({ variant, size, iconPlacement, iconClass, aIcon, isExternalLink, children }) => {
  const EXTERNAL_LINK_A11Y_TEXT = ', opens in a new window';

  // Default to existing logic for icon sizing if no aIcon.size is provided
  let resolvedIconSize: AIconSize = AIconSize.S;
  if (variant === MButtonType.Tertiary) {
    resolvedIconSize = AIconSize.S;
  } else if (size === MButtonSize.L) {
    resolvedIconSize = AIconSize.L;
  }

  // If user provides aIcon and includes size, try to parse it or default
  if (aIcon?.size) {
    // If a user sets aIcon.size to a valid AIconSize or a custom string, rely on it as-is
    resolvedIconSize = (aIcon.size as AIconSize) || AIconSize.S;
  }

  // Decide whether to show leading/trailing icons
  const shouldShowLeadingIcon = iconPlacement === MButtonIconPlacement.Leading;
  const shouldShowTrailingIcon =
    variant === MButtonType.Tertiary
      ? iconPlacement !== MButtonIconPlacement.Leading
      : iconPlacement === MButtonIconPlacement.Trailing;

  const defaultLeadingIconClass =
    variant === MButtonType.Tertiary ? MButtonIconClass.BackArrow : MButtonIconClass.Search;
  const defaultTrailingIconClass =
    variant === MButtonType.Tertiary ? MButtonIconClass.ForwardArrow : MButtonIconClass.ArrowRight;

  // If user passes aIcon, prefer aIcon.icon
  const leadingIconClass = iconClass ?? (aIcon?.icon ? aIcon.icon : defaultLeadingIconClass);
  const trailingIconClass = iconClass ?? (aIcon?.icon ? aIcon.icon : defaultTrailingIconClass);

  return (
    <>
      {!isExternalLink && shouldShowLeadingIcon && (
        <AIcon icon={leadingIconClass} size={resolvedIconSize} data-testid={`${TEST_ID}-leading-icon`} />
      )}
      <MButtonLabel className="btn-label">{children}</MButtonLabel>
      {isExternalLink ? (
        <AIcon
          icon={MButtonIconClass.ExternalArrow}
          size={resolvedIconSize}
          data-testid={`${TEST_ID}-trailing-icon`}
          aria-label={EXTERNAL_LINK_A11Y_TEXT}
        />
      ) : (
        shouldShowTrailingIcon && (
          <AIcon icon={trailingIconClass} size={resolvedIconSize} data-testid={`${TEST_ID}-trailing-icon`} />
        )
      )}
    </>
  );
};

const PolymorphicMButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, MButtonProps<ElementType>>(
  <C extends ElementType = 'button', P = Record<string, unknown>>(
    {
      as,
      buttonText,
      children,
      variant = MButtonType.Primary,
      size = MButtonSize.M,
      iconPlacement = MButtonIconPlacement.None,
      iconClass,
      aIcon,
      isExternal = false,
      disabled = false,
      type = 'button',
      target,
      href,
      className,
      trackingProperties,
      custom_click_track_value,
      preTrackVal,
      ...rest
    }: MButtonProps<C, P>,
    ref: MButtonRef<C>
  ) => {
    const renderAsLink = !!href;
    const extTargetVal = '_blank';
    const isExternalLink = renderAsLink && (isExternal || target === extTargetVal);

    const buttonTypeClass = {
      'm-button-primary': variant === MButtonType.Primary,
      'm-button-secondary': variant === MButtonType.Secondary,
      'm-link-tertiary-button': variant === MButtonType.Tertiary && !isExternalLink,
      'm-link-tertiary-button-external': variant === MButtonType.Tertiary && isExternalLink,
    };

    const buttonSizeClass =
      variant === MButtonType.Tertiary
        ? {}
        : {
            'm-button-s': size === MButtonSize.S,
            'm-button-m': size === MButtonSize.M,
            'm-button-l': size === MButtonSize.L,
          };

    const iconPlacementClass = {
      'icon-leading': iconPlacement === MButtonIconPlacement.Leading && !isExternalLink,
      'icon-trailing': iconPlacement === MButtonIconPlacement.Trailing || isExternalLink,
    };

    const mergedClassName = clsx(className, buttonTypeClass, buttonSizeClass, iconPlacementClass, { disabled });

    const polymorphicProps = renderAsLink
      ? {
          href,
          target: isExternalLink ? extTargetVal : target,
          ...(disabled ? { 'aria-disabled': true } : {}),
        }
      : { type, disabled };

    const MButtonTag = (as ?? (renderAsLink ? 'a' : 'button')) as ElementType;

    const defaultRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
    const elementRef = (ref ?? defaultRef) as React.RefObject<HTMLAnchorElement | HTMLButtonElement>;

    useTracking({
      ref: elementRef,
      trackingProperties,
      custom_click_track_value,
      preTrackVal,
      text: buttonText,
    });

    return (
      <MButtonTag
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        role={renderAsLink ? 'link' : 'button'}
        ref={elementRef as React.Ref<HTMLAnchorElement | HTMLButtonElement>}
        as={as}
        className={mergedClassName}
        variant={variant}
        {...polymorphicProps}
        {...rest}
      >
        <MButtonContent
          isExternalLink={isExternalLink}
          variant={variant}
          size={size}
          iconPlacement={iconPlacement}
          iconClass={iconClass}
          aIcon={aIcon}
        >
          {buttonText}
          {typeof children === 'string' ? ` ${children}` : children}
        </MButtonContent>
      </MButtonTag>
    );
  }
);

export const MButton = createStyledButton(PolymorphicMButton);

export { MButtonIconPlacement, MButtonIconClass, MButtonSize, MButtonType, TEST_ID as M_BUTTON_TEST_ID };
